import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Bell,
  Briefcase,
  CornerDownLeft,
  Gamepad2,
  Gift,
  Home,
  Inbox,
  LayoutDashboard,
  Lightbulb,
  MessageCircle,
  Plus,
  Search,
  ShieldCheck,
  Store,
  Ticket,
  TrendingUp,
  Trophy,
  User,
  UserPlus,
  Users,
  Zap,
} from "lucide-react";
import { selectShowcaseBets } from "../../redux/slices/betHouseSlice";
import { selectAppIsAuthenticated } from "../redux/appAuthSlice";
import { CREATE_BET_HOUSE_PATH } from "../utils/createBetHouseHelpers";
import { isProductEnabled } from "../config/products";

/**
 * CommandPalette — keyboard-first launcher.
 * Opens with ⌘K / Ctrl+K. Search across sections, bet houses, and quick actions.
 */

const SECTION_COMMANDS = [
  { id: "house", label: "BondHouse", hint: "Bet houses", to: "/app/bethouse", Icon: Home, group: "Navigate", product: "house" },
  { id: "p2p", label: "BondP2P", hint: "Peer-to-peer bets", to: "/app/p2p", Icon: Users, group: "Navigate", product: "p2p" },
  { id: "predict", label: "BondPredict", hint: "Make predictions", to: "/app/bond-predict", Icon: TrendingUp, group: "Navigate", product: "predict" },
  { id: "play", label: "BondPlay", hint: "Casino games", to: "/app/bond-play", Icon: Gamepad2, group: "Navigate", product: "play" },
  { id: "tips", label: "Tips", hint: "Community calls", to: "/app/predictions", Icon: Lightbulb, group: "Community", product: "tips" },
  { id: "leaderboard", label: "Leaderboard", hint: "Rankings & scores", to: "/app/leaderboard", Icon: Trophy, group: "Community", product: "leaderboard" },
  { id: "referrals", label: "Referrals", hint: "Invite & earn", to: "/app/referrals", Icon: Gift, group: "Community", authRequired: true },
  { id: "chat", label: "BondChat", hint: "Social pulse", to: "/app/general-chat", Icon: MessageCircle, group: "Navigate", product: "chat" },
  { id: "activity", label: "Activity Pulse", hint: "Your account story", to: "/app/activity", Icon: Bell, group: "Navigate" },
  { id: "community", label: "Community Guidelines", hint: "Rules & standards", to: "/app/community", Icon: ShieldCheck, group: "Navigate" },
];

const MANAGE_COMMANDS = [
  { id: "joined", label: "My tickets", hint: "Joined bets", to: "/app/joined-bets", Icon: Ticket, group: "Manage", product: "house" },
  { id: "requests", label: "Requests", hint: "P2P invites", to: "/app/requests", Icon: Inbox, group: "Manage" },
  { id: "markets", label: "Bet Markets", hint: "Manage your houses", to: "/app/bethouse/manage", Icon: Store, group: "Manage", product: "house" },
  { id: "portfolio", label: "BondPredict Portfolio", hint: "Your market holdings", to: "/app/bond-predict/portfolio", Icon: Briefcase, group: "Manage", product: "predict" },
];

const ACCOUNT_COMMANDS = [
  { id: "dashboard", label: "Dashboard", to: "/app/dashboard", Icon: LayoutDashboard, group: "Account" },
  { id: "profile", label: "Profile", to: "/app/profile", Icon: User, group: "Account" },
];

const ACTION_COMMANDS = [
  { id: "create", label: "Create a Bet House", hint: "Open the launch flow", to: CREATE_BET_HOUSE_PATH, Icon: Plus, group: "Actions", product: "house" },
  { id: "create-p2p", label: "Create P2P Bet", hint: "Start a peer challenge", to: "/app/p2p/create", Icon: UserPlus, group: "Actions", authRequired: true, product: "p2p" },
];

const matchScore = (q, label, hint) => {
  if (!q) return 1;
  const text = `${label} ${hint || ""}`.toLowerCase();
  return text.includes(q.toLowerCase()) ? 1 : 0;
};

const CommandPalette = ({ open, onClose }) => {
  const navigate = useNavigate();
  const housesRaw = useSelector(selectShowcaseBets);
  const houses = useMemo(() => housesRaw || [], [housesRaw]);
  const isAuthenticated = useSelector(selectAppIsAuthenticated);
  const chatUnread = useSelector((s) => s.generalChat?.unreadCount || 0);
  const activityUnread = useSelector((s) => s.activities?.unreadCount || 0);

  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const items = useMemo(() => {
    const enabled = (c) => isProductEnabled(c.product);
    const sections = SECTION_COMMANDS.filter(
      (c) => enabled(c) && (!c.authRequired || isAuthenticated)
    ).map((c) => {
      let badge = null;
      if (c.id === "chat" && chatUnread > 0) badge = chatUnread;
      if (c.id === "activity" && activityUnread > 0) badge = activityUnread;
      return { ...c, badge };
    });
    const account = isAuthenticated ? ACCOUNT_COMMANDS : [];
    const manage = isAuthenticated
      ? MANAGE_COMMANDS.filter(enabled)
      : [];
    const actions = ACTION_COMMANDS.filter(
      (c) => enabled(c) && (!c.authRequired || isAuthenticated)
    );

    const houseItems = isProductEnabled("house")
      ? houses.slice(0, 6).map((h) => ({
          id: `house-${h._id}`,
          label: h.name || "Unnamed house",
          hint: `${h.fixtures?.length || 0} fixtures`,
          to: `/app/bethouse/${h._id}`,
          Icon: Zap,
          group: "Houses",
        }))
      : [];

    const all = [...sections, ...houseItems, ...actions, ...manage, ...account];
    if (!query.trim()) return all;
    return all.filter((c) => matchScore(query, c.label, c.hint));
  }, [query, houses, isAuthenticated, chatUnread, activityUnread]);

  useEffect(() => {
    if (!open) return undefined;
    setQuery("");
    setActiveIndex(0);
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(items.length - 1, i + 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(0, i - 1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const target = items[activeIndex];
        if (target?.to) {
          navigate(target.to);
          onClose();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, items, activeIndex, navigate, onClose]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-cmd-idx="${activeIndex}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  if (!open) return null;

  // Group items for display while preserving global activeIndex
  let runningIndex = -1;
  const groupOrder = ["Navigate", "Houses", "Actions", "Manage", "Account"];
  const grouped = groupOrder
    .map((g) => ({
      group: g,
      items: items.filter((i) => i.group === g),
    }))
    .filter((g) => g.items.length > 0);

  return createPortal(
    <div className="cmd-root" role="dialog" aria-modal="true" aria-label="Command palette">
      <button type="button" className="cmd-backdrop" aria-label="Close" onClick={onClose} />
      <div className="cmd-shell">
        <div className="cmd-input-row">
          <Search size={16} className="cmd-input-icon" aria-hidden />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Jump to anything…   try ‘tickets’, ‘requests’, ‘chat’"
            className="cmd-input"
            spellCheck={false}
          />
          <span className="cmd-kbd-hint">
            <kbd>esc</kbd> close
          </span>
        </div>

        <div ref={listRef} className="cmd-list">
          {items.length === 0 && (
            <div className="cmd-empty">
              <Search size={20} />
              <p>Nothing matches “{query}”.</p>
              <span>Try a section name, “create”, “tickets”, or a house.</span>
            </div>
          )}

          {grouped.map(({ group, items: groupItems }) => (
            <div key={group} className="cmd-group">
              <p className="cmd-group-label">{group}</p>
              {groupItems.map((c) => {
                runningIndex += 1;
                const idx = runningIndex;
                const Icon = c.Icon;
                const active = idx === activeIndex;
                return (
                  <button
                    key={c.id}
                    type="button"
                    data-cmd-idx={idx}
                    className={`cmd-item ${active ? "cmd-item-active" : ""}`}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => {
                      if (c.to) {
                        navigate(c.to);
                        onClose();
                      }
                    }}
                  >
                    <span className="cmd-item-icon">
                      <Icon size={15} strokeWidth={2.2} />
                    </span>
                    <span className="cmd-item-body">
                      <span className="cmd-item-label">{c.label}</span>
                      {c.hint && <span className="cmd-item-hint">{c.hint}</span>}
                    </span>
                    {c.badge ? <span className="cmd-item-badge">{c.badge > 99 ? "99+" : c.badge}</span> : null}
                    {active && (
                      <span className="cmd-item-enter" aria-hidden>
                        <CornerDownLeft size={12} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="cmd-foot">
          <span className="cmd-foot-hint">
            <kbd>↑</kbd>
            <kbd>↓</kbd>
            navigate
          </span>
          <span className="cmd-foot-hint">
            <kbd>↵</kbd>
            open
          </span>
          <span className="cmd-foot-brand">BondLauncher</span>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CommandPalette;
